import React, { useState } from 'react';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Form, PrimaryButton, FieldTextEditor, NamedLink, Modal } from '../../components';
import { ensureCurrentUser } from '../../util/data';
import * as validators from '../../util/validators';

import { BsCheckCircle, BsFillCheckCircleFill } from 'react-icons/bs';
import { FaFolderOpen, FaPen } from 'react-icons/fa6';

import css from './ProjectProposalForm.css';

// Helper function to check if HTML content from Quill editor has actual text
const hasTextContent = htmlString => {
  if (!htmlString) return false;
  // Strip HTML tags and check if there's any non-whitespace content
  const textContent = htmlString.replace(/<[^>]*>/g, '').trim();
  return textContent.length > 0;
};

const ProjectProposalFormComponent = props => {
  const { portfolioWorks = [], onFetchPortfolioWorks, initialValues = {}, onValuesChange } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [portfolioDropdownOpen, setPortfolioDropdownOpen] = useState(false);
  // Both mirror form values, so a restored draft (see ProjectProposalPage) seeds them too.
  const [selectedPortfolioWorks, setSelectedPortfolioWorks] = useState(
    initialValues.portfolioWorkIds || []
  );
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [showSimilarWorkText, setShowSimilarWorkText] = useState(
    hasTextContent(initialValues.similarWork)
  );

  // Field-level validator for Quill rich-text fields: rejects empty HTML
  // wrappers like "<p><br></p>" that pass the plain `required` validator.
  const requiredRichText = message => value => (hasTextContent(value) ? undefined : message);

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={fieldRenderProps => {
        const {
          currentUser,
          handleSubmit,
          submitInProgress,
          intl,
          onManageDisableScrolling,
          values,
        } = fieldRenderProps;

        const bestFitValue = values['bestFit'];
        const similarWorkValue = values['similarWork'];
        const questionsValue = values['questions'];

        // Check if similarWork field has actual text content (not just empty HTML)
        const hasSimilarWorkContent = hasTextContent(similarWorkValue);

        // Form is valid if user has provided either portfolio works OR similarWork text
        const hasRelevantExperience = selectedPortfolioWorks.length > 0 || hasSimilarWorkContent;

        const submitDisabled =
          submitInProgress ||
          !hasTextContent(bestFitValue) ||
          !hasTextContent(questionsValue) ||
          !hasRelevantExperience;

        const ensuredCurrentUser = ensureCurrentUser(currentUser);
        const currentUserLoaded = !!ensuredCurrentUser.id;

        const cantSendProposals =
          currentUserLoaded &&
          currentUser?.profileImage === null &&
          currentUser?.attributes.profile.bio === null;

        const heading = intl.formatMessage({
          id: 'ProjectProposalForm.heading',
        });

        const bestFitLabel = intl.formatMessage({
          id: 'ProjectProposalForm.bestFitLabel',
        });

        const similarWorkLabelBase = intl.formatMessage({
          id: 'ProjectProposalForm.similarWorkLabel',
        });

        const similarWorkLabelOptional = intl.formatMessage({
          id: 'ProjectProposalForm.similarWorkLabelOptional',
        });

        const similarWorkLabel =
          selectedPortfolioWorks.length > 0 ? similarWorkLabelOptional : similarWorkLabelBase;

        const questionsLabel = intl.formatMessage({
          id: 'ProjectProposalForm.questionsLabel',
        });

        const submitButtonText = intl.formatMessage({
          id: 'ProjectProposalForm.submitButtonText',
        });

        const bestFitPlaceholder = intl.formatMessage({
          id: 'ProjectProposalForm.bestFitPlaceholder',
        });

        const similarWorkPlaceholder = intl.formatMessage({
          id: 'ProjectProposalForm.similarWorkPlaceholder',
        });

        const questionsPlaceholder = intl.formatMessage({
          id: 'ProjectProposalForm.questionsPlaceholder',
        });

        const requiredMessage = intl.formatMessage({
          id: 'ProjectProposalForm.requiredMessage',
        });

        return (
          <div className={css.root}>
            {cantSendProposals ? (
              <div className={css.inputContainer}>
                <h2>
                  <FormattedMessage id="ProjectProposalForm.headingCantEnquire" />
                </h2>
                <p>
                  <FormattedMessage id="ProjectProposalForm.messageCantEnquire" />
                </p>
                <NamedLink name="ProfileSettingsPage">
                  <FormattedMessage id="ProjectProposalForm.buttonCantEnquire" />
                </NamedLink>
              </div>
            ) : (
              <Form onSubmit={handleSubmit} className={css.formContainer}>
                {onValuesChange ? (
                  <FormSpy
                    subscription={{ values: true }}
                    onChange={({ values }) => onValuesChange(values)}
                  />
                ) : null}
                <div className={css.inputContainer}>
                  <h3 className={css.heading}>{heading}</h3>
                  <FieldTextEditor
                    className={css.quill}
                    name="bestFit"
                    id="bestFit"
                    label={bestFitLabel}
                    placeholder={bestFitPlaceholder}
                    validate={requiredRichText(requiredMessage)}
                    isApplicationForm={true}
                  />

                  {/* Optional "Show relevant experience" section */}
                  <div className={css.relevantExperienceSection}>
                    <div className={css.relevantExperienceHeader}>
                      <div className={css.relevantExperienceTitle}>
                        {intl.formatMessage({
                          id: 'ProjectProposalForm.relevantExperienceTitle',
                        })}
                      </div>
                    </div>

                    {/* Selected works display */}
                    {selectedPortfolioWorks.length > 0 && (
                      <div className={css.selectedWorksContainer}>
                        {selectedPortfolioWorks.map(workId => {
                          const work = portfolioWorks.find(w => w.id.uuid === workId);
                          if (!work) return null;

                          const images = work.images || [];
                          const firstImage = images.length > 0 ? images[0] : null;

                          return (
                            <div key={workId} className={css.selectedWorkChip}>
                              {firstImage && (
                                <div className={css.selectedWorkThumbnail}>
                                  <img
                                    src={
                                      firstImage.attributes.variants['square-small']?.url ||
                                      firstImage.attributes.variants['square-small2x']?.url
                                    }
                                    alt={work.attributes.title}
                                  />
                                </div>
                              )}
                              <span className={css.selectedWorkTitle}>{work.attributes.title}</span>
                              <button
                                type="button"
                                className={css.removeWorkButton}
                                onClick={() => {
                                  const newSelected = selectedPortfolioWorks.filter(
                                    id => id !== workId
                                  );
                                  setSelectedPortfolioWorks(newSelected);
                                  // Update form values
                                  fieldRenderProps.form.change('portfolioWorkIds', newSelected);
                                }}
                                aria-label="Remove"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className={css.buttonsContainer}>
                      <button
                        type="button"
                        className={css.addWorksButton}
                        onClick={() => setPortfolioDropdownOpen(!portfolioDropdownOpen)}
                      >
                        <span className={css.addWorksButtonIcon}>
                          <FaFolderOpen />
                        </span>
                        {selectedPortfolioWorks.length === 0
                          ? intl.formatMessage({ id: 'ProjectProposalForm.selectWorks' })
                          : intl.formatMessage({ id: 'ProjectProposalForm.addMoreWorks' })}
                      </button>
                      <div className={css.orDivider}>
                        <span className={css.orDividerText}>
                          {intl.formatMessage({ id: 'ProjectProposalForm.or' })}
                        </span>
                      </div>

                      <button
                        type="button"
                        className={css.showSimilarWorkButton}
                        onClick={() => setShowSimilarWorkText(true)}
                      >
                        <span className={css.addWorksButtonIcon}>
                          <FaPen />
                        </span>
                        {intl.formatMessage({ id: 'ProjectProposalForm.writeAboutSimilarWork' })}
                      </button>
                    </div>

                    {/* Portfolio works picker modal */}
                    {portfolioDropdownOpen && (
                      <div className={css.portfolioWorksPicker}>
                        <div className={css.portfolioWorksPickerHeader}>
                          <div className={css.portfolioWorksPickerHeaderContainer}>
                            <h4 className={css.portfolioWorksPickerTitle}>
                              {intl.formatMessage({
                                id: 'ProjectProposalForm.selectPortfolioWorks',
                              })}
                            </h4>
                            <p className={css.portfolioWorksPickerSubtitle}>
                              {intl.formatMessage({
                                id: 'ProjectProposalForm.selectPortfolioWorksSubtitle',
                              })}
                            </p>
                          </div>
                          <button
                            type="button"
                            className={css.portfolioWorksPickerClose}
                            onClick={() => setPortfolioDropdownOpen(false)}
                          >
                            ×
                          </button>
                        </div>
                        <div className={css.portfolioWorksGrid}>
                          {portfolioWorks.map(work => {
                            const images = work.images || [];
                            const firstImage = images.length > 0 ? images[0] : null;
                            const isSelected = selectedPortfolioWorks.includes(work.id.uuid);

                            return (
                              <div
                                key={work.id.uuid}
                                className={`${css.portfolioWorkCard} ${
                                  isSelected ? css.portfolioWorkCardSelected : ''
                                }`}
                                onClick={() => {
                                  const workId = work.id.uuid;
                                  let newSelected;
                                  if (isSelected) {
                                    newSelected = selectedPortfolioWorks.filter(
                                      id => id !== workId
                                    );
                                  } else {
                                    newSelected = [...selectedPortfolioWorks, workId];
                                  }
                                  setSelectedPortfolioWorks(newSelected);
                                  fieldRenderProps.form.change('portfolioWorkIds', newSelected);
                                }}
                              >
                                {firstImage && (
                                  <div className={css.portfolioWorkCardImage}>
                                    <img
                                      src={
                                        firstImage.attributes.variants['square-small']?.url ||
                                        firstImage.attributes.variants['square-small2x']?.url
                                      }
                                      alt={work.attributes.title}
                                    />
                                    {isSelected && (
                                      <div className={css.portfolioWorkCardCheckmark}>
                                        <BsCheckCircle />
                                      </div>
                                    )}
                                    <div className={css.portfolioWorkCardTitle}>
                                      {work.attributes.title}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Create new work card */}
                          <div
                            className={css.createNewWorkCard}
                            onClick={() => {
                              setShowRefreshButton(true);
                              window.open('/work/new', '_blank');
                            }}
                          >
                            <div className={css.createNewWorkCardContent}>
                              <div className={css.createNewWorkIcon}>+</div>
                              <div className={css.createNewWorkText}>
                                {intl.formatMessage({
                                  id: 'ProjectProposalForm.createNewWork',
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={css.portfolioWorksPickerFooter}>
                          {showRefreshButton && (
                            <button
                              type="button"
                              className={css.portfolioWorksPickerRefresh}
                              onClick={async () => {
                                if (onFetchPortfolioWorks) {
                                  await onFetchPortfolioWorks();
                                  setShowRefreshButton(false);
                                }
                              }}
                            >
                              {intl.formatMessage({ id: 'ProjectProposalForm.refresh' })}
                            </button>
                          )}
                          <button
                            type="button"
                            className={css.portfolioWorksPickerDone}
                            onClick={() => setPortfolioDropdownOpen(false)}
                          >
                            {intl.formatMessage({ id: 'ProjectProposalForm.done' })}
                          </button>
                        </div>
                      </div>
                    )}

                    {showSimilarWorkText ? (
                      <div className={css.similarWorkTextContainer}>
                        <FieldTextEditor
                          className={css.quill}
                          name="similarWork"
                          id="similarWork"
                          label={similarWorkLabel}
                          placeholder={similarWorkPlaceholder}
                          isApplicationForm={true}
                        />
                      </div>
                    ) : null}
                  </div>

                  <FieldTextEditor
                    className={`${css.quill} ${css.questionsQuill}`}
                    name="questions"
                    id="questions"
                    label={questionsLabel}
                    placeholder={questionsPlaceholder}
                    validate={requiredRichText(requiredMessage)}
                    isApplicationForm={true}
                  />

                  <div className={css.submitContainer}>
                    <PrimaryButton
                      type="button"
                      className={css.submitButton}
                      onClick={() => setModalOpen(true)}
                      disabled={submitDisabled}
                    >
                      {submitInProgress
                        ? intl.formatMessage({ id: 'ProjectProposalForm.submittingText' })
                        : submitButtonText}
                    </PrimaryButton>
                  </div>
                </div>

                <Modal
                  id="ProjectProposalForm.terms"
                  isOpen={modalOpen}
                  onClose={() => setModalOpen(false)}
                  onManageDisableScrolling={onManageDisableScrolling}
                >
                  <div className={css.checkboxContainer}>
                    <div className={css.tosText}>
                      <h4 className={css.tosTitle}>
                        <FormattedMessage id="ProjectProposalForm.tosReminderTitle" />
                      </h4>
                      <p className={css.tosDescription}>
                        <FormattedMessage id="ProjectProposalForm.checkboxTOS" />
                      </p>
                      <div className={css.tosTextItem}>
                        <BsFillCheckCircleFill className={css.checkIcon} />
                        <FormattedMessage id="ProjectProposalForm.checkboxTOS1" />
                      </div>
                      <div className={css.tosTextItem}>
                        <BsFillCheckCircleFill className={css.checkIcon} />
                        <FormattedMessage id="ProjectProposalForm.checkboxTOS2" />
                      </div>
                    </div>
                  </div>
                  <PrimaryButton
                    type="submit"
                    className={css.submitButton}
                    disabled={submitInProgress}
                  >
                    {submitInProgress
                      ? intl.formatMessage({ id: 'ProjectProposalForm.submittingText' })
                      : submitButtonText}
                  </PrimaryButton>
                </Modal>
              </Form>
            )}
          </div>
        );
      }}
    />
  );
};

const ProjectProposalForm = compose(injectIntl)(ProjectProposalFormComponent);

ProjectProposalForm.displayName = 'ProjectProposalForm';

export default ProjectProposalForm;
